import { useEffect } from 'react';
import { useLayout } from './LayoutProvider';

/**
 * Marks the topmost visible section active, where "topmost" means earliest in
 * `ids` (registry/document order) among the entries currently intersecting —
 * not a sort on `boundingClientRect.top`, which is only valid at the instant
 * the entry crossed the observer's edge and goes stale as the page scrolls.
 * Observes #section-<id> roots.
 */
export function useScrollSpy(ids: string[]) {
  const { setActiveSection } = useLayout();
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(`section-${id}`)).filter((e): e is HTMLElement => !!e);
    if (els.length === 0) return;
    const visible = new Set<string>();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const id = (e.target as HTMLElement).dataset.section ?? '';
        if (e.isIntersecting) visible.add(id); else visible.delete(id);
      }
      const top = ids.find((id) => visible.has(id));
      if (top) setActiveSection(top);
    }, { rootMargin: '-80px 0px -60% 0px', threshold: 0 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids.join('|'), setActiveSection]);   // eslint-disable-line react-hooks/exhaustive-deps
}
